import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { catalogueApi } from '../lib/api';
import { useReorderQuote, ReorderQuoteCard } from '../components/ReorderQuoteCard';
import { Header } from '../components/Header';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
    ArrowLeft, Package, ShoppingCart, DollarSign, RefreshCw,
    Disc, Calendar, Hash,
    TrendingUp, ArrowUpRight, ArrowDownRight, ArrowRight, Minus, AlertCircle,
    Clock, Target, Sparkles
} from 'lucide-react';

const INITIAL_COLORS = [
    'bg-violet-500', 'bg-blue-500', 'bg-emerald-500', 'bg-amber-500',
    'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-teal-500',
];

// ─── Inventory Intelligence (Details — full insights) ───
const TREND_LABEL = {
    up:      { label: 'Increasing', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: ArrowUpRight },
    down:    { label: 'Decreasing', color: 'text-rose-700',    bg: 'bg-rose-50 border-rose-200',       icon: ArrowDownRight },
    stable:  { label: 'Stable',     color: 'text-slate-600',   bg: 'bg-slate-50 border-slate-200',     icon: ArrowRight },
    no_data: { label: 'No data yet',color: 'text-slate-400',   bg: 'bg-slate-50 border-slate-200',     icon: Minus },
};

const _formatShortDate = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso + 'T00:00:00Z');
        return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return iso; }
};

const _formatDaysHuman = (days) => {
    if (days == null) return '—';
    if (days <= 0) return 'now';
    if (days < 7) return `${Math.round(days)} day${Math.round(days) === 1 ? '' : 's'}`;
    if (days < 60) return `~${Math.round(days / 7)} week${Math.round(days / 7) === 1 ? '' : 's'}`;
    return `~${Math.round(days / 30)} month${Math.round(days / 30) === 1 ? '' : 's'}`;
};

const _confidenceBand = (score) => {
    if (score >= 70) return { label: 'High',     color: 'text-emerald-700', bg: 'bg-emerald-100', bar: 'bg-emerald-500' };
    if (score >= 45) return { label: 'Medium',   color: 'text-amber-700',   bg: 'bg-amber-100',   bar: 'bg-amber-500' };
    if (score > 0)   return { label: 'Low',      color: 'text-rose-700',    bg: 'bg-rose-100',    bar: 'bg-rose-500' };
    return             { label: 'No history', color: 'text-slate-500',   bg: 'bg-slate-100',   bar: 'bg-slate-300' };
};

const MetricTile = ({ label, value, sub, testId, className }) => (
    <div className={`rounded-lg border border-slate-200 bg-white p-3 ${className || ''}`} data-testid={testId}>
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{label}</div>
        <div className="mt-1 text-lg font-semibold text-slate-900 truncate">{value ?? '—'}</div>
        {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
);

// ─── Inventory Intelligence — Assistant Mode (v1.1) ───
// The card is intentionally narrative-first: recommendation on top,
// followed by the reasoning ("Why?"), key numbers, a decay-line chart,
// stock risk, a confidence explanation, a Demand-Signals-coming-soon
// placeholder, and a closing AI Insight. Deterministic templated text —
// swappable for an LLM narrative later without touching this component.
const RISK_META = {
    high:    { label: 'High',      color: 'text-rose-700',    bg: 'bg-rose-50',    border: 'border-rose-300',    dot: 'bg-rose-500' },
    medium:  { label: 'Medium',    color: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-300',   dot: 'bg-amber-500' },
    low:     { label: 'Low',       color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-300', dot: 'bg-emerald-500' },
    unknown: { label: 'Unknown',   color: 'text-slate-500',   bg: 'bg-slate-50',   border: 'border-slate-200',   dot: 'bg-slate-400' },
};

// SVG line chart showing stock declining from today until run-out.
// Vertical markers annotate the recommended-reorder date and the
// projected run-out date so production planners can eyeball where the
// safe reorder window sits relative to today.
const StockDecayChart = ({ startStock, avgDaily, coverageDays, leadTimeDays, reorderDate, runoutDate }) => {
    if (!startStock || !avgDaily || avgDaily <= 0) return null;
    const width = 640;
    const height = 180;
    const padL = 40, padR = 20, padT = 12, padB = 32;
    // X-axis: today → coverageDays days forward. We extend to at least the run-out date.
    const daysToRunout = Math.ceil(startStock / avgDaily);
    const totalDays = Math.max(coverageDays || 120, daysToRunout);
    const xForDay = (d) => padL + (d / totalDays) * (width - padL - padR);
    const yForStock = (s) => padT + (1 - s / startStock) * (height - padT - padB);
    // Points: today at full stock, run-out day at 0.
    const linePoints = `${xForDay(0)},${yForStock(startStock)} ${xForDay(daysToRunout)},${yForStock(0)}`;
    // Filled area: same line + baseline
    const areaPoints = `${xForDay(0)},${yForStock(startStock)} ${xForDay(daysToRunout)},${yForStock(0)} ${xForDay(daysToRunout)},${height - padB} ${padL},${height - padB}`;
    // Marker helpers
    const reorderOffset = reorderDate ? Math.max(0, Math.ceil((new Date(reorderDate) - new Date()) / (1000 * 60 * 60 * 24))) : null;
    const runoutOffset = runoutDate ? Math.ceil((new Date(runoutDate) - new Date()) / (1000 * 60 * 60 * 24)) : daysToRunout;
    // X-axis ticks: today, halfway, run-out.
    const ticks = [
        { d: 0, label: 'Today' },
        { d: reorderOffset, label: 'Reorder by' },
        { d: runoutOffset, label: 'Run-out' },
    ].filter((t) => t.d != null);

    return (
        <div className="w-full overflow-x-auto" data-testid="ii-stock-decay-chart">
            <svg viewBox={`0 0 ${width} ${height}`} width="100%" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Stock decay forecast">
                <defs>
                    <linearGradient id="decayFill" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%"   stopColor="#8b5cf6" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.02" />
                    </linearGradient>
                </defs>
                {/* Y baseline */}
                <line x1={padL} x2={width - padR} y1={height - padB} y2={height - padB} stroke="#e2e8f0" strokeWidth="1" />
                {/* Area + line */}
                <polygon points={areaPoints} fill="url(#decayFill)" />
                <polyline points={linePoints} fill="none" stroke="#7c3aed" strokeWidth="2" />
                {/* Reorder marker (amber vertical) */}
                {reorderOffset != null && reorderOffset < totalDays && (
                    <>
                        <line x1={xForDay(reorderOffset)} x2={xForDay(reorderOffset)} y1={padT} y2={height - padB}
                              stroke="#d97706" strokeWidth="1.5" strokeDasharray="4 3" />
                        <text x={xForDay(reorderOffset)} y={padT + 8} fill="#92400e" fontSize="10" textAnchor="middle" fontWeight="600">
                            Reorder
                        </text>
                    </>
                )}
                {/* Run-out marker (rose vertical) */}
                <line x1={xForDay(runoutOffset)} x2={xForDay(runoutOffset)} y1={padT} y2={height - padB}
                      stroke="#e11d48" strokeWidth="1.5" strokeDasharray="4 3" />
                <text x={xForDay(runoutOffset)} y={padT + 8} fill="#9f1239" fontSize="10" textAnchor="middle" fontWeight="600">
                    Run-out
                </text>
                {/* Y labels: start stock, zero */}
                <text x={padL - 6} y={yForStock(startStock) + 3} fill="#64748b" fontSize="10" textAnchor="end">{startStock.toLocaleString()}</text>
                <text x={padL - 6} y={height - padB + 3} fill="#64748b" fontSize="10" textAnchor="end">0</text>
                {/* X-axis ticks */}
                {ticks.map((t, i) => (
                    <g key={i}>
                        <line x1={xForDay(t.d)} x2={xForDay(t.d)} y1={height - padB} y2={height - padB + 3} stroke="#94a3b8" />
                        <text x={xForDay(t.d)} y={height - padB + 16} fill="#64748b" fontSize="10" textAnchor="middle">{t.label}</text>
                    </g>
                ))}
            </svg>
        </div>
    );
};

const InventoryIntelligenceCard = ({ entry }) => {
    const forecast = entry?.forecast;
    if (!forecast) return null;
    const daily = forecast.avg_sales_per_day || 0;
    const trend = TREND_LABEL[forecast.trend] || TREND_LABEL.no_data;
    const TrendIcon = trend.icon;
    const confidence = _confidenceBand(forecast.confidence_score || 0);
    const risk = RISK_META[forecast.stock_risk || 'unknown'];
    const stock = entry.stock_quantity;
    const stockDisplay = stock == null ? '—' : stock.toLocaleString();
    const coverageLabel = forecast.coverage_days != null
        ? `${_formatDaysHuman(forecast.coverage_days)}`
        : '—';
    const hasAnySignal = daily > 0 || (stock != null && stock > 0) || forecast.suggested_reorder_qty;
    if (!hasAnySignal) return null;

    return (
        <Card className="border-slate-200" data-testid="inventory-intelligence-card">
            <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-violet-500" />
                    Inventory Intelligence
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* ── 1. AI Recommendation ── */}
                {forecast.recommendation_text && (
                    <div
                        className="rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-4"
                        data-testid="ii-recommendation"
                    >
                        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold text-violet-700">
                            <Sparkles className="h-3 w-3" />
                            Recommendation
                        </div>
                        <p className="text-sm text-slate-800 mt-1.5 leading-relaxed">
                            {forecast.recommendation_text}
                        </p>
                    </div>
                )}

                {/* ── 2. Why? ── */}
                {forecast.why_bullets?.length > 0 && (
                    <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-3" data-testid="ii-why">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">Why?</div>
                        <ul className="text-sm text-slate-700 space-y-1 list-disc list-inside">
                            {forecast.why_bullets.map((b, i) => (
                                <li key={i}>{b}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* ── 3. Key numbers row ── */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <MetricTile label="Current Stock" value={stockDisplay}
                        sub={stock == null ? 'Not tracked' : `Threshold ${entry.low_stock_threshold || 50}`}
                        testId="ii-current-stock" />
                    <MetricTile label="Current Coverage" value={coverageLabel}
                        sub={forecast.coverage_days != null ? 'At current pace' : 'Add sales history'}
                        testId="ii-coverage" />
                    <MetricTile label="Avg Sales / Day" value={daily > 0 ? daily.toFixed(daily < 1 ? 2 : 1) : '—'}
                        sub={daily > 0
                            ? `${forecast.avg_sales_per_week}/wk · ${forecast.avg_sales_per_month}/mo`
                            : 'No orders in 90 days'}
                        testId="ii-avg-daily" />
                    <MetricTile label="Suggested Reorder"
                        value={forecast.suggested_reorder_qty ? forecast.suggested_reorder_qty.toLocaleString() : '—'}
                        sub={forecast.suggested_reorder_qty
                            ? `by ${_formatShortDate(forecast.recommended_reorder_date)}`
                            : 'No suggestion yet'}
                        testId="ii-suggested-qty" />
                </div>

                {/* ── 4. Stock Decay Chart (replaces 4-bar) ── */}
                {stock != null && stock > 0 && daily > 0 && (
                    <div className="rounded-lg border border-slate-100 bg-white p-3">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
                            Stock forecast
                        </div>
                        <StockDecayChart
                            startStock={stock}
                            avgDaily={daily}
                            coverageDays={forecast.settings?.coverage_days || 120}
                            leadTimeDays={forecast.settings?.lead_time_days || 60}
                            reorderDate={forecast.recommended_reorder_date}
                            runoutDate={forecast.predicted_runout_date}
                        />
                    </div>
                )}

                {/* ── 5. Stock Risk + Trend + Confidence row ── */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className={`rounded-lg border p-3 ${risk.bg} ${risk.border}`} data-testid="ii-stock-risk">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Stock Risk</div>
                        <div className={`mt-1 flex items-center gap-1.5 text-lg font-semibold ${risk.color}`}>
                            <span className={`h-2.5 w-2.5 rounded-full ${risk.dot}`}></span>
                            {risk.label}
                        </div>
                        <div className="text-xs text-slate-600 mt-1 leading-snug">
                            {forecast.stock_risk_reason}
                        </div>
                    </div>

                    <div className={`rounded-lg border p-3 ${trend.bg}`} data-testid="ii-trend-analysis">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Demand Trend</div>
                        <div className={`mt-1 flex items-center gap-1.5 text-lg font-semibold ${trend.color}`}>
                            <TrendIcon className="h-5 w-5" /> {trend.label}
                        </div>
                        <div className="text-xs text-slate-600 mt-1">
                            {forecast.sales_last_30d} vs {forecast.sales_prev_30d} units (30d vs prev)
                            {forecast.trend_delta_pct != null && (
                                <> · {forecast.trend_delta_pct > 0 ? '+' : ''}{forecast.trend_delta_pct}%</>
                            )}
                        </div>
                    </div>

                    <div className={`rounded-lg border border-slate-200 bg-white p-3`} data-testid="ii-confidence">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Confidence</div>
                        <div className="mt-1 flex items-center gap-2">
                            <span className={`text-lg font-semibold ${confidence.color}`}>
                                {forecast.confidence_score || 0}
                                <span className="text-sm text-slate-400 font-normal">/100</span>
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${confidence.bg} ${confidence.color}`}>
                                {confidence.label}
                            </span>
                        </div>
                        <div className="mt-1.5 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full ${confidence.bar} transition-all`}
                                 style={{ width: `${forecast.confidence_score || 0}%` }} />
                        </div>
                        {forecast.confidence_reasons?.length > 0 && (
                            <ul className="text-[11px] text-slate-500 mt-2 space-y-0.5">
                                {forecast.confidence_reasons.map((r, i) => (
                                    <li key={i}>• {r}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* ── 6. Demand Signals (placeholder — Version 2) ── */}
                <div
                    className="rounded-lg border border-dashed border-slate-200 bg-slate-50/40 p-3"
                    data-testid="ii-demand-signals-placeholder"
                >
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                            Demand Signals
                        </div>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-medium">
                            Coming soon
                        </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                        {[
                            { label: 'Streaming', hint: 'Spotify + Apple' },
                            { label: 'Search',    hint: 'Google Trends' },
                            { label: 'Social',    hint: 'IG + TikTok' },
                            { label: 'Internal',  hint: 'Order history' },
                        ].map((s) => (
                            <div key={s.label} className="rounded border border-slate-100 bg-white p-2">
                                <div className="text-slate-700 font-medium">{s.label}</div>
                                <div className="text-slate-400">— <span className="text-[10px]">{s.hint}</span></div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                        <span>Overall Demand Signal Score</span>
                        <span className="text-slate-400">— / 100</span>
                    </div>
                </div>

                {/* ── 7. AI Insight ── */}
                {forecast.ai_insight_text && (
                    <div
                        className="rounded-lg border border-violet-100 bg-gradient-to-r from-violet-50 to-transparent p-3 flex items-start gap-2"
                        data-testid="ii-ai-insight"
                    >
                        <Sparkles className="h-4 w-4 text-violet-500 mt-0.5 flex-shrink-0" />
                        <div>
                            <div className="text-[11px] uppercase tracking-wide font-semibold text-violet-700">
                                AI Insight
                            </div>
                            <p className="text-sm text-slate-800 mt-0.5 leading-relaxed">
                                {forecast.ai_insight_text}
                            </p>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

const getInitialColor = (str) => {
    let hash = 0;
    for (let i = 0; i < (str || '').length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return INITIAL_COLORS[Math.abs(hash) % INITIAL_COLORS.length];
};

const getStockHealth = (entry) => {
    if (entry.stock_quantity === null || entry.stock_quantity === undefined) return null;
    const threshold = entry.low_stock_threshold || 50;
    const qty = entry.stock_quantity;
    if (qty <= 0) return { level: 'critical', color: 'bg-red-500', barColor: 'bg-red-400', text: 'Out of stock', textColor: 'text-red-600', bgTint: 'bg-red-50' };
    if (qty <= threshold * 0.5) return { level: 'critical', color: 'bg-red-500', barColor: 'bg-red-400', text: 'Critical', textColor: 'text-red-600', bgTint: 'bg-red-50' };
    if (qty <= threshold) return { level: 'low', color: 'bg-amber-400', barColor: 'bg-amber-300', text: 'Low stock', textColor: 'text-amber-600', bgTint: 'bg-amber-50' };
    return { level: 'good', color: 'bg-emerald-500', barColor: 'bg-emerald-300', text: 'In stock', textColor: 'text-emerald-600', bgTint: 'bg-emerald-50' };
};

const getStockPercent = (entry) => {
    if (entry.stock_quantity === null || entry.stock_quantity === undefined) return 0;
    const threshold = entry.low_stock_threshold || 50;
    return Math.min(100, (entry.stock_quantity / (threshold * 3)) * 100);
};

const formatMediaType = (entry) => {
    const mt = (entry.media_type || '').toLowerCase();
    if (mt === 'cd' || entry.media_type === 'CD') return 'CD';
    if (mt === 'dvd') return 'DVD';
    if (mt === 'blu_ray' || mt === 'bluray') return 'Blu-ray';
    if (mt === 'digital' || entry.media_type === 'Digital') return 'Digital';
    // Only append the inches symbol for numeric vinyl formats.
    if (entry.format && /^\d+$/.test(entry.format)) return `${entry.format}" Vinyl`;
    if (mt === 'vinyl' || entry.media_type === 'Vinyl') return 'Vinyl';
    return null;
};

const formatRunway = (days) => {
    if (days === null || days === undefined) return null;
    if (days <= 0) return 'Out of stock';
    if (days < 7) return `~${Math.round(days)} day${Math.round(days) !== 1 ? 's' : ''} remaining`;
    if (days < 30) return `~${Math.round(days / 7)} week${Math.round(days / 7) !== 1 ? 's' : ''} remaining`;
    if (days < 365) return `~${Math.round(days / 30)} month${Math.round(days / 30) !== 1 ? 's' : ''} remaining`;
    return `~${Math.round(days / 365)} year${Math.round(days / 365) !== 1 ? 's' : ''} remaining`;
};

const STATUS_COLORS = {
    'Draft': 'bg-slate-100 text-slate-600',
    'Submitted': 'bg-blue-100 text-blue-700',
    'In Progress': 'bg-amber-100 text-amber-700',
    'On Hold': 'bg-orange-100 text-orange-800',
    'Shipping': 'bg-indigo-100 text-indigo-700',
    'Completed': 'bg-emerald-100 text-emerald-700',
    'Cancelled': 'bg-red-100 text-red-700',
};

export default function CatalogueDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reordering, setReordering] = useState(false);
    const [reorderOpen, setReorderOpen] = useState(false);
    const [reorderQty, setReorderQty] = useState('250');
    const [reorderPolicy, setReorderPolicy] = useState(null);
    const [quoteComment, setQuoteComment] = useState('');

    useEffect(() => {
        // Prefetch the reorder policy so the dialog opens with the
        // right minimum instantly. Non-fatal on failure — the dialog
        // will lazy-load it on first open.
        catalogueApi.reorderPolicy(id).then(
            (res) => setReorderPolicy(res.data),
            () => {},
        );
    }, [id]);

    useEffect(() => {
        const fetchDetail = async () => {
            try {
                const res = await catalogueApi.getDetail(id);
                setDetail(res.data);
            } catch (error) {
                toast.error('Failed to load catalogue entry');
                navigate('/catalogue');
            } finally {
                setLoading(false);
            }
        };
        fetchDetail();
    }, [id, navigate]);

    const openReorderDialog = async () => {
        // Effective minimum comes from the two-layer policy (entry
        // override → product-family default). Fall back to the local
        // suggested-qty logic while the API call is in flight so the
        // dialog opens instantly.
        let policy = reorderPolicy;
        if (!policy) {
            try {
                const res = await catalogueApi.reorderPolicy(id);
                policy = res.data;
                setReorderPolicy(policy);
            } catch {
                policy = { min_qty: 1, source: 'default', product_family: 'vinyl' };
            }
        }
        const suggested =
            detail?.entry?.defaults_json?.quantity ||
            detail?.entry?.auto_reorder_quantity ||
            policy.min_qty;
        const defaultQty = Math.max(policy.min_qty, parseInt(suggested, 10) || policy.min_qty);
        setReorderQty(String(defaultQty));
        setReorderOpen(true);
    };

    // Live reorder quote — shared with the dashboard's Quick-Reorder
    // modal via `useReorderQuote`, so what the customer sees here IS
    // what they'll see from either entry point. The hook only fires
    // when `id` AND a positive qty are present; pass `undefined` while
    // the dialog is closed so we don't waste a request on every page
    // visit.
    const { quote, loading: quoteLoading } = useReorderQuote(
        reorderOpen ? id : undefined,
        reorderQty,
    );

    const handleReorder = async () => {
        const qty = parseInt(reorderQty, 10);
        const minQty = reorderPolicy?.min_qty ?? 1;
        if (!qty || qty < minQty) {
            const fam = (reorderPolicy?.product_family || '').toUpperCase();
            toast.error(`Minimum reorder quantity${fam ? ` for ${fam}` : ''} is ${minQty} units`);
            return;
        }
        setReordering(true);
        try {
            const res = await catalogueApi.reorder(id, qty);
            // The reorder endpoint returns `pricing_warning` when the
            // catalogue entry has no linked SKU — keep the user informed
            // instead of silently dropping the issue.
            if (res.data.pricing_warning) {
                toast.warning(res.data.pricing_warning);
            } else {
                toast.success(res.data.message);
            }
            setReorderOpen(false);
            navigate(`/orders/${res.data.order_id}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create reorder');
        } finally {
            setReordering(false);
        }
    };

    const handleRequestQuote = async () => {
        const qty = parseInt(reorderQty, 10) || undefined;
        const comment = (quoteComment || '').trim();
        if (!comment) {
            toast.error('A quote request comment is required');
            return;
        }
        setReordering(true);
        try {
            const res = await catalogueApi.requestQuote(id, { quantity: qty, comment });
            toast.success(res.data.message || 'Quote request submitted');
            setReorderOpen(false);
            setQuoteComment('');
            navigate(`/orders/${res.data.order_id}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to request quote');
        } finally {
            setReordering(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center h-[calc(100vh-4rem)]"><LoadingSpinner size="lg" /></div>
            </div>
        );
    }

    if (!detail) return null;

    const { entry, orders, stats } = detail;
    const health = getStockHealth(entry);
    const initials = entry.title ? entry.title.substring(0, 2).toUpperCase() : '??';
    const bgColor = getInitialColor(entry.catalogue_number);
    const media = formatMediaType(entry);

    return (
        <div className="min-h-screen bg-background" data-testid="catalogue-detail-page">
            <Header />
            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

                {/* Back */}
                <button onClick={() => navigate('/catalogue')}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6 transition">
                    <ArrowLeft className="h-4 w-4" /> Back to catalogue
                </button>

                {/* ─── Header ─── */}
                <div className="flex flex-col sm:flex-row gap-6 mb-8">
                    {/* Art */}
                    {entry.cover_image ? (
                        <div className="rounded-xl h-40 w-40 sm:h-48 sm:w-48 flex-shrink-0 overflow-hidden">
                            <img src={entry.cover_image} alt={entry.title || entry.catalogue_number}
                                className="w-full h-full object-cover" />
                        </div>
                    ) : (
                        <div className={`${bgColor} rounded-xl h-40 w-40 sm:h-48 sm:w-48 flex-shrink-0 flex items-center justify-center`}>
                            <span className="text-5xl font-bold text-white/90">{initials}</span>
                        </div>
                    )}

                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900 mb-1">{entry.title || entry.catalogue_number}</h1>
                                <p className="text-base text-slate-500">{entry.artist || 'Unknown artist'}</p>
                                <div className="flex items-center gap-3 mt-3">
                                    <span className="font-mono text-sm text-slate-400">{entry.catalogue_number}</span>
                                    {media && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                                            {media}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Reorder button */}
                            <Button onClick={openReorderDialog} disabled={reordering}
                                className="bg-slate-900 hover:bg-slate-800 shrink-0" data-testid="reorder-btn">
                                <RefreshCw className={`h-4 w-4 mr-2 ${reordering ? 'animate-spin' : ''}`} />
                                {reordering ? 'Creating...' : 'Reorder'}
                            </Button>
                        </div>

                        {/* Stock bar (large) */}
                        {health && (
                            <div className={`mt-5 p-4 rounded-xl ${health.bgTint} border border-slate-100`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-slate-700">Stock Level</span>
                                    <span className={`text-sm font-semibold ${health.textColor}`}>
                                        {health.text}
                                        {stats.stock_runway_days != null && (
                                            <span className="font-normal"> · {formatRunway(stats.stock_runway_days)}</span>
                                        )}
                                    </span>
                                </div>
                                <div className="h-2 w-full bg-white/60 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full transition-all ${health.color}`} style={{ width: `${getStockPercent(entry)}%` }} />
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                    <span className={`text-sm font-bold ${health.textColor}`}>
                                        {entry.stock_quantity.toLocaleString()} units
                                        {stats.stock_runway_days == null && stats.total_orders > 0 && entry.stock_quantity > 0 && (
                                            <span className="font-normal text-xs text-slate-400"> · Slow moving</span>
                                        )}
                                        {stats.stock_runway_days == null && stats.total_orders === 0 && entry.stock_quantity > 0 && (
                                            <span className="font-normal text-xs text-slate-400"> · No usage data</span>
                                        )}
                                    </span>
                                    <span className="text-xs text-slate-400">Threshold: {entry.low_stock_threshold || 50}</span>
                                </div>
                                {/* Reorder recommendation */}
                                {health.level !== 'good' && stats.stock_runway_days != null && stats.stock_runway_days < 30 && (
                                    <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-100" data-testid="reorder-recommendation">
                                        <span className="text-xs text-amber-600 font-medium">Reorder recommended soon</span>
                                    </div>
                                )}
                                {entry.auto_reorder_enabled && (
                                    <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-100">
                                        <RefreshCw className="h-3.5 w-3.5 text-violet-500" />
                                        <span className="text-xs text-violet-600 font-medium">
                                            Auto reorder enabled &middot; {entry.auto_reorder_quantity?.toLocaleString() || '—'} units
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* ─── Inventory Intelligence ─── */}
                <div className="mb-6">
                    <InventoryIntelligenceCard entry={entry} />
                </div>

                {/* ─── Order History ─── */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base font-semibold text-slate-900">Order History</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {orders.length === 0 ? (
                            <div className="py-12 text-center">
                                <ShoppingCart className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                                <p className="text-sm text-slate-500">No orders yet for this title</p>
                                <Button variant="outline" size="sm" className="mt-3" onClick={openReorderDialog} disabled={reordering}>
                                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Create first order
                                </Button>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-background hover:bg-background">
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">Order #</TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">Status</TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">Qty</TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">Amount</TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">Date</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {orders.map(order => (
                                        <TableRow key={order.id} className="cursor-pointer hover:bg-slate-50"
                                            onClick={() => navigate(`/orders/${order.id}`)} data-testid={`order-row-${order.order_number}`}>
                                            <TableCell className="font-mono text-sm">
                                                #{String(order.order_number).padStart(6, '0')}
                                            </TableCell>
                                            <TableCell>
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[order.status] || 'bg-slate-100 text-slate-600'}`}>
                                                    {order.status}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-sm text-slate-600">
                                                {order.quantity != null ? order.quantity.toLocaleString() : '—'}
                                            </TableCell>
                                            <TableCell className="text-sm text-slate-600">
                                                {order.invoice_total != null
                                                    ? `${order.invoice_total.toLocaleString('da-DK', { minimumFractionDigits: 2 })} ${order.invoice_currency || ''}`
                                                    : '—'}
                                            </TableCell>
                                            <TableCell className="text-sm text-slate-500">
                                                {new Date(order.created_at).toLocaleDateString('da-DK')}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </main>

            {/* Reorder quantity dialog */}
            <Dialog open={reorderOpen} onOpenChange={(open) => { setReorderOpen(open); if (!open) setQuoteComment(''); }}>
                <DialogContent className="max-w-md" data-testid="reorder-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {quote?.requires_quote
                                ? `Request quote — ${entry.title || entry.catalogue_number}`
                                : `Reorder ${entry.title || entry.catalogue_number}`}
                        </DialogTitle>
                        <DialogDescription>
                            {quote?.requires_quote
                                ? 'This product does not have a fixed price. Submit your request with a short note and we will send you a quote to review.'
                                : 'This creates a new draft order linked to this catalogue entry. Master & artwork are reused from the previous run — no test pressings.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500">Catalogue #</span>
                            <span className="font-medium text-slate-900">{entry.catalogue_number}</span>
                        </div>
                        {media && (
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">Format</span>
                                <span className="font-medium text-slate-900">{media}</span>
                            </div>
                        )}
                        <div>
                            <Label htmlFor="reorder-qty">Quantity to produce</Label>
                            <Input
                                id="reorder-qty"
                                type="number"
                                min={reorderPolicy?.min_qty ?? 1}
                                step={1}
                                value={reorderQty}
                                onChange={(e) => setReorderQty(e.target.value)}
                                onFocus={(e) => e.target.select()}
                                className="mt-1"
                                data-testid="reorder-quantity-input"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                                {reorderPolicy ? (
                                    <>
                                        Minimum {reorderPolicy.product_family?.toUpperCase() || ''} reorder is{' '}
                                        <strong>{reorderPolicy.min_qty} units</strong>
                                        {reorderPolicy.source === 'entry_override'
                                            ? ' (override for this title).'
                                            : ' (family default).'}{' '}
                                        Suggested based on previous orders.
                                    </>
                                ) : (
                                    'Loading minimum quantity…'
                                )}
                            </p>
                            {parseInt(reorderQty, 10) > 0 && reorderPolicy && parseInt(reorderQty, 10) < reorderPolicy.min_qty && (
                                <p className="text-xs text-red-600 mt-1" data-testid="reorder-qty-min-error">
                                    Quantity must be at least {reorderPolicy.min_qty} units.
                                </p>
                            )}
                        </div>

                        {quote?.requires_quote ? (
                            <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="quote-request-block">
                                <div className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
                                    Quote required
                                </div>
                                <p className="text-xs text-amber-800 leading-snug">
                                    This SKU has no fixed price. Tell us what you need and we will send you a
                                    quote as soon as possible.
                                </p>
                                <div>
                                    <Label htmlFor="quote-comment" className="text-xs text-amber-900">
                                        Quote request comment <span className="text-red-600">*</span>
                                    </Label>
                                    <textarea
                                        id="quote-comment"
                                        value={quoteComment}
                                        onChange={(e) => setQuoteComment(e.target.value)}
                                        rows={3}
                                        placeholder="e.g. Need 500 units in transparent red for release on 1 Sept."
                                        className="mt-1 w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                                        data-testid="quote-comment-textarea"
                                        required
                                    />
                                </div>
                            </div>
                        ) : (
                            /* Live pricing preview — shared component with the
                               dashboard Quick-Reorder modal so the two surfaces
                               always show the same estimated number. */
                            <ReorderQuoteCard quote={quote} loading={quoteLoading} />
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReorderOpen(false)} disabled={reordering} data-testid="reorder-cancel-btn">
                            Cancel
                        </Button>
                        {quote?.requires_quote ? (
                            <Button
                                onClick={handleRequestQuote}
                                disabled={reordering || !quoteComment.trim()}
                                className="bg-amber-600 hover:bg-amber-700"
                                data-testid="request-quote-confirm-btn"
                            >
                                {reordering ? (
                                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>
                                ) : (
                                    <>Request quote</>
                                )}
                            </Button>
                        ) : (
                            <Button onClick={handleReorder} disabled={reordering || parseInt(reorderQty, 10) < 250} className="bg-slate-900 hover:bg-slate-800" data-testid="reorder-confirm-btn">
                                {reordering ? (
                                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Creating…</>
                                ) : (
                                    <><RefreshCw className="h-4 w-4 mr-2" /> Create reorder</>
                                )}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
